import React from "react";
import { DatasetsByCubeIdDocument, useDeleteDatasetMutation, DatasetPreviewDocument } from "graphql/types/graphql";
import GenericDeleteButton from "./GenericDeleteButton";
import { ContextData } from "@/utils/Provider";

interface DeleteDatasetButtonProps {
  datasetId: string;
  onDelete?: () => void;
  datasetName: string;
  disableDeleteButton?: boolean;
  confirmationMessage?: string;
}

type DatasetRefetchQueries = [
  {
    query: typeof DatasetsByCubeIdDocument;
    variables: {
      cubeId: string;
      includeAlreadyMerged: boolean;
    };
  },
  {
    query: typeof DatasetPreviewDocument;
    variables: {
      filter: {
        cubeName: string;
        datasetId: string;
      };
    };
  }
];

const DeleteDatasetButton: React.FC<DeleteDatasetButtonProps> = ({
  datasetId,
  onDelete,
  datasetName,
  disableDeleteButton = false,
  confirmationMessage = "Are you sure you want to delete this dataset?",
}) => {
  const [deleteDataset, { loading }] = useDeleteDatasetMutation({
    update: (cache) => {
      cache.evict({
        id: "ROOT_QUERY",
        fieldName: "datasetsByCubeId",
      });
    },
  });

  const getMutationVariables = (itemId: string, contextData: ContextData) => {
    const { selectedCube } = contextData;
    return {
      cubeName: selectedCube.name,
      datasetId: itemId,
    };
  };

  const getRefetchQueries = (itemId: string, contextData: ContextData) => {
    const { selectedCube } = contextData;
    return [
      {
        query: DatasetsByCubeIdDocument,
        variables: { cubeId: selectedCube?.cubeId, includeAlreadyMerged: false },
      },
      {
        query: DatasetPreviewDocument,
        variables: {
          filter: {
            cubeName: selectedCube?.name,
            datasetId: itemId,
          },
        },
      },
    ] as DatasetRefetchQueries;
  };

  return (
    <GenericDeleteButton
      itemId={datasetId}
      onDelete={onDelete}
      itemName={datasetName}
      confirmationMessage={confirmationMessage}
      disableDeleteButton={disableDeleteButton}
      mutationFn={deleteDataset}
      mutationLoading={loading}
      getMutationVariables={getMutationVariables}
      getRefetchQueries={getRefetchQueries}
      successMessage="Dataset deleted successfully!"
      deleteTooltip="Delete Dataset"
    />
  );
};

export default DeleteDatasetButton;
